import { Icon, IconButton, makeStyles, Snackbar, SnackbarContent } from '@material-ui/core';
import React from 'react';

import { IReactChildren, IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { whiteColor } from '~/src/dash/utils/material-dashboard-react';

export interface ISnackbarProps extends IWithStylesProps, IReactChildren {
  open: boolean;
  // different styled snackbar messages display depending on if there is an error or warning
  error?: boolean;
  warning?: boolean;
  onClose?: () => void;
  enableTopPosition?: boolean;
  enableCenterPosition?: boolean;
  successBar?: boolean;
  messageColor?: string;
  backgroundColor?: string;
  closeColor?: string;
  autoHideDuration?: number;
}

export interface ISnackbarState {
  error: boolean;
  warning: boolean;
  message: string;
}

export const generateSnackbarState = (): ISnackbarState => {
  return {
    error: false,
    warning: false,
    message: '',
  };
};

const useStyles = makeStyles((theme: any) => ({
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  warning: {
    backgroundColor: whiteColor,
  },
  info: {
    backgroundColor: theme.palette.primary.main,
  },
  success: {
    backgroundColor: theme.palette.success.main,
  },
  icon: {
    fontSize: '1rem',
    marginRight: '1rem',
  },
  message: {
    display: 'inline-flex',
    'align-items': 'center',
  },
}));

const getClass = (successBar: boolean, error: boolean, warning: boolean): string => {
  const classes = useStyles();
  if (successBar) {
    return classes.success;
  } else if (error) {
    return classes.error;
  } else if (warning) {
    return classes.warning;
  }
  return classes.info;
};

const getMessageColor = (messageColor: string, warning: boolean) => {
  if (messageColor) {
    return { color: messageColor };
  } else if (warning) {
    return { color: 'black' };
  }
  return {};
};

const getCloseColor = (closeColor: string, warning: boolean) => {
  if (closeColor) {
    return closeColor;
  } else if (warning) {
    return 'black';
  }
  return 'inherit';
};

export const SnackbarView = decorate(
  {},
  function ({
    enableTopPosition,
    enableCenterPosition,
    open,
    error,
    warning,
    children,
    successBar,
    onClose,
    backgroundColor,
    messageColor,
    closeColor,
    autoHideDuration,
  }: ISnackbarProps) {
    const classes = useStyles();
    const snackbarClass = getClass(successBar || false, error || false, warning || false);
    const snackbarMessageColor = getMessageColor(messageColor || '', warning || false);
    const snackbarCloseColor = getCloseColor(closeColor || '', warning || false);

    return (
      <Snackbar
        anchorOrigin={{
          vertical: enableTopPosition ? 'top' : 'bottom',
          horizontal: enableCenterPosition ? 'center' : 'right',
        }}
        open={open}
        autoHideDuration={autoHideDuration ? autoHideDuration : 6000}
        onClose={onClose}
      >
        <SnackbarContent
          className={snackbarClass}
          aria-describedby='client-snackbar-model-delete-dialog'
          style={
            backgroundColor
              ? { backgroundColor, whiteSpace: 'pre-line' }
              : { whiteSpace: 'pre-line' }
          }
          elevation={2}
          message={
            <span
              id='client-snackbar-model-delete-dialog'
              className={classes.message}
              style={snackbarMessageColor}
            >
              {warning && (
                <img
                  src='/assets/shared/svg/NoData.svg'
                  style={{ width: 35, height: 36, marginRight: '10px' }}
                />
              )}
              {!warning && (
                <Icon className={classes.icon} style={error ? {} : { color: '#4FC1B5' }}>
                  {error ? 'warning' : 'check_circle'}
                </Icon>
              )}
              {children}
            </span>
          }
          action={[
            // @ts-ignore
            <IconButton key='close' aria-label='Close' color={snackbarCloseColor} onClick={onClose}>
              <Icon>close</Icon>
            </IconButton>,
          ]}
        />
      </Snackbar>
    );
  },
);
